import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { MessageSquare, Send, CheckCircle2, Mail, Share2, ChevronRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TYPE_CONFIG = {
  support_ticket: { label: "Support", className: "bg-slate-100 text-slate-600" },
  broadcast: { label: "Announcement", className: "bg-blue-100 text-blue-700" },
  intra_org_share: { label: "Shared Search", className: "bg-teal-100 text-teal-700" },
};
const CAT_CONFIG = {
  bug: { label: "Bug", className: "bg-red-100 text-red-700" },
  consult: { label: "Consult", className: "bg-teal-100 text-teal-700" },
  question: { label: "Question", className: "bg-blue-100 text-blue-700" },
  feedback: { label: "Feedback", className: "bg-slate-100 text-slate-600" },
};

export default function MessagesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const loadMessages = async (t) => {
    setLoading(true);
    try { const r = await api.get(`/messages?tab=${t}`); setMessages(r.data); } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadMessages(tab); }, [tab]); // eslint-disable-line

  const loadThread = async (id) => {
    setSelectedId(id);
    try { const r = await api.get(`/messages/${id}/thread`); setThread(r.data); } catch { toast.error("Failed to load thread"); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    setReplying(true);
    try {
      await api.post(`/messages/${selectedId}/reply`, { content: replyText });
      setReplyText("");
      loadThread(selectedId);
      toast.success("Reply sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setReplying(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading text-slate-900">Messages</h1>
          <p className="text-sm text-slate-500 mt-1">Support tickets, announcements, and shared searches</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setSelectedId(null); setThread(null); }}>
        <TabsList className="mb-4">
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
        </TabsList>

        <div className="flex gap-4 min-h-[500px]">
          {/* Message List */}
          <div className="w-80 flex-shrink-0 space-y-2 overflow-y-auto">
            {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white rounded-lg border animate-pulse" />) :
              messages.length === 0 ? (
                <Card className="border-dashed"><CardContent className="p-8 text-center"><MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">No messages</p></CardContent></Card>
              ) : messages.map(m => (
                <button key={m.id} onClick={() => loadThread(m.id)} data-testid={`msg-${m.id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedId === m.id ? "border-lime-500 bg-lime-50/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Badge className={`text-[10px] border-0 ${TYPE_CONFIG[m.type]?.className || "bg-slate-100 text-slate-600"}`}>{TYPE_CONFIG[m.type]?.label || m.type}</Badge>
                    {m.metadata?.ticket_category && <Badge className={`text-[10px] border-0 ${CAT_CONFIG[m.metadata.ticket_category]?.className || ""}`}>{CAT_CONFIG[m.metadata.ticket_category]?.label}</Badge>}
                    {m.status === "resolved" && <Badge className="bg-lime-100 text-lime-700 text-[10px] border-0">Resolved</Badge>}
                  </div>
                  <p className="text-sm text-slate-900 line-clamp-1 font-medium">{m.sender_name}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{m.content?.slice(0, 80)}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-slate-400 font-mono">{new Date(m.created_at).toLocaleDateString()}</span>
                    {m.reply_count > 0 && <span className="text-[10px] text-slate-400">{m.reply_count} replies</span>}
                  </div>
                </button>
              ))
            }
          </div>

          {/* Thread View */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
            {!thread ? (
              <div className="flex-1 flex items-center justify-center"><p className="text-sm text-slate-400">Select a message to view</p></div>
            ) : (
              <>
                {thread.root?.status === "resolved" && (
                  <div className="bg-lime-50 border-b border-lime-200 px-4 py-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-lime-600" />
                    <span className="text-sm text-lime-700 font-accent">This conversation has been resolved</span>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {[thread.root, ...(thread.replies || [])].map((m, i) => (
                    <div key={m.id} className={`${m.sender_role === "master_admin" ? "ml-8" : "mr-8"}`}>
                      <div className={`rounded-lg p-3 ${m.sender_role === "master_admin" ? "bg-evergreen-950 text-white" : "bg-slate-50 border border-slate-200"}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-accent font-medium ${m.sender_role === "master_admin" ? "text-lime-300" : "text-slate-700"}`}>{m.sender_name}</span>
                          <span className={`text-[10px] font-mono ${m.sender_role === "master_admin" ? "text-slate-400" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</span>
                        </div>
                        <p className={`text-sm leading-relaxed ${m.sender_role === "master_admin" ? "text-slate-200" : "text-slate-600"}`}>{m.content}</p>
                      </div>
                      {i === 0 && m.linked_entity_type === "role_search" && m.linked_entity_id && (
                        <button onClick={() => navigate(`/search/${m.linked_entity_id}`)} className="mt-1 text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> View linked search
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {thread.root?.status !== "resolved" && (
                  <div className="border-t border-slate-200 p-3 flex gap-2">
                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..." className="text-sm min-h-[40px] flex-1" data-testid="reply-input" />
                    <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="bg-evergreen-950 hover:bg-evergreen-900 text-white self-end" data-testid="reply-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
}
